// /frontend/src/components/common/PromotionalPopup.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

// Import gambar dari assets - UPDATED
import dsc1 from '../../assets/DSC_1.jpg';
import dsc2 from '../../assets/DSC_2.jpg';
import dsc3 from '../../assets/DSC_3.jpg';
import dsc4 from '../../assets/DSC_4.jpg';
import dsc5 from '../../assets/DSC_5.jpg';

const PromotionalPopup = ({ isOpen, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Promotional slides dengan deskripsi dan button
  const slides = [
    {
      id: 1,
      image: dsc1,
      title: "Quality & Craftsmanship",
      subtitle: "Crafted from top-grade plantation teak. Designed with purpose. Built to last",
      description: "Crafted from top-grade plantation teak. Designed with purpose. Built to last.",
      buttonText: "Shop Now",
      buttonLink: "/category/all",
      backgroundColor: "from-teal-600 to-teal-800"
    },
    {
      id: 2,
      image: dsc2,
      title: "Customization & Collection Depth",
      subtitle: "Choose from over 110 mix-and-match pieces. Personalize every detail — from drawer fronts to fabric textures",
      description: "Choose from over 110 mix-and-match pieces. Personalize every detail — from drawer fronts to fabric textures.",
      buttonText: "Get Started",
      buttonLink: "/custom-design",
      backgroundColor: "from-blue-600 to-blue-800"
    },
    {
      id: 3,
      image: dsc3,
      title: "Design Meets Practicality",
      subtitle: "Where form meets function. Each piece balances beauty and utility — for real life in refined spaces",
      description: "Where form meets function. Each piece balances beauty and utility — for real life in refined spaces.",
      buttonText: "View Packages",
      buttonLink: "/designer-packages",
      backgroundColor: "from-purple-600 to-purple-800"
    },
    {
      id: 4,
      image: dsc4,
      title: "Barefoot Luxury Living",
      subtitle: "Effortless Sophistication with a Coastal Soul",
      description: "Effortless Sophistication with a Coastal Soul.",
      buttonText: "View Packages",
      buttonLink: "/designer-packages",
      backgroundColor: "from-purple-600 to-purple-800"
    },
    {
      id: 5,
      image: dsc5,
      title: "Made-to-Order Process",
      subtitle: "Everything is made to order. Delivered in 4–6 months — styled, placed, and ready for living",
      description: "Everything is made to order. Delivered in 4–6 months — styled, placed, and ready for living.",
      buttonText: "View Packages",
      buttonLink: "/designer-packages",
      backgroundColor: "from-purple-600 to-purple-800"
    }
  ];

  // Navigation functions
  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsAnimating(false), 300);
  }, [isAnimating, slides.length]);

  const prevSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsAnimating(false), 300);
  }, [isAnimating, slides.length]);

  const goToSlide = useCallback((index) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 300);
  }, [isAnimating, currentSlide]);

  // Auto-advance slideshow setiap 5 detik
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [isOpen, nextSlide]);

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event) => {
    if (!isOpen) return;
    
    switch (event.key) {
      case 'ArrowLeft':
        prevSlide();
        break;
      case 'ArrowRight':
        nextSlide();
        break;
      case 'Escape':
        onClose();
        break;
      default:
        // Do nothing for other keys
        break;
    }
  }, [isOpen, prevSlide, nextSlide, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-3"
      onClick={handleBackdropClick}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[1200px] max-h-[95vh] overflow-hidden">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-30 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white"
          aria-label="Close popup"
        >
          <FaTimes className="h-5 w-5" />
        </button>

        {/* Slideshow Container */}
        <div className="relative h-[600px] md:h-[700px] overflow-hidden rounded-2xl">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                index === currentSlide
                  ? 'opacity-100 transform translate-x-0'
                  : index < currentSlide
                  ? 'opacity-0 transform -translate-x-full'
                  : 'opacity-0 transform translate-x-full'
              }`}
            >
              {/* Gambar dengan overlay text */}
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ 
                  backgroundImage: `url(${slide.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black to-transparent opacity-60"></div>
              </div>

              {/* Content Overlay */}
              <div className="relative z-20 h-full flex items-center">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
                  <div className="max-w-2xl">
                    <div className="opacity-100">
                      <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                        {slide.title}
                      </h1>
                      <h2 className="text-2xl md:text-3xl text-gray-200 mb-6 font-light">
                        {slide.subtitle}
                      </h2>
                      {/* <p className="text-lg md:text-xl text-gray-200 mb-8 leading-relaxed max-w-lg">
                        {slide.description}
                      </p> */}
                      {/* <Link
                        to={slide.buttonLink}
                        onClick={onClose}
                        className={`inline-flex items-center px-10 py-5 bg-gradient-to-r ${slide.backgroundColor} text-white font-bold rounded-xl shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 text-lg`}
                      >
                        {slide.buttonText}
                        <svg className="ml-3 h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </Link> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Controls */}
        <div className="absolute top-1/2 left-4 transform -translate-y-1/2 z-30">
          <button
            onClick={prevSlide}
            disabled={isAnimating}
            className="bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="Previous slide"
          >
            <FaChevronLeft className="h-5 w-5" />
          </button>
        </div>

        <div className="absolute top-1/2 right-4 transform -translate-y-1/2 z-30">
          <button
            onClick={nextSlide}
            disabled={isAnimating}
            className="bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="Next slide"
          >
            <FaChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3 z-30">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 ${
                index === currentSlide
                  ? 'bg-white scale-125 shadow-lg'
                  : 'bg-white bg-opacity-50 hover:bg-opacity-75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-black bg-opacity-30 z-30 rounded-b-2xl">
          <div
            className="h-full bg-white transition-all duration-700 ease-linear rounded-bl-2xl"
            style={{
              width: `${((currentSlide + 1) / slides.length) * 100}%`
            }}
          />
        </div>

        {/* Slide Counter */}
        <div className="absolute top-4 left-4 z-30 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm font-medium">
          {currentSlide + 1} / {slides.length}
        </div>
      </div>
    </div>
  );
};

export default PromotionalPopup;